import { Button, Card, CardHeader, EmptyState, Skeleton, formatDate, useToast } from "@helmr/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { changePtTrainer, completePt, listAffected, listUnmarked, noShowPt, type PtSession } from "../../api/pt";
import { useServices } from "../../app/services";
import { addDays } from "../../lib/dates";
import { Failure, Field, FormError, messageOf, styles as c, useScope } from "./common";
import k from "./classes.module.css";

/** Booked sessions dated yesterday or earlier: nobody marked them. For the trainer or manager who can edit sessions. */
export function UnmarkedSessions({ nameOf, onOpen }: { nameOf: (id: string | null, known?: string | null) => string; onOpen: (s: PtSession) => void }) {
  const { client } = useServices();
  const sc = useScope();
  const qc = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const yesterday = addDays(sc.date, -1);
  const q = useQuery({
    queryKey: ["pt", "unmarked", ...sc.key, yesterday],
    queryFn: () => listUnmarked(client, { from: addDays(sc.date, -90), to: yesterday, branchId: sc.branchId }).then((r) => r.sessions),
    retry: false,
  });
  const mark = useMutation({
    mutationFn: (v: { id: string; kind: "complete" | "no_show" }) => (v.kind === "complete" ? completePt(client, v.id) : noShowPt(client, v.id)),
    onSuccess: (_r, v) => { setError(null); void qc.invalidateQueries({ queryKey: ["pt"] }); toast.show(v.kind === "complete" ? "Session completed" : "Marked no-show"); },
    onError: (e) => setError(messageOf(e)),
  });
  return (
    <Card aria-label="Unmarked sessions">
      <CardHeader title="Unmarked sessions" subtitle="Booked sessions from yesterday or earlier that nobody marked. Mark each one so the pack and the trainer's pay are right." />
      {q.isPending ? <div aria-busy="true" aria-label="Loading unmarked sessions" className={k.skGrid}><Skeleton width="40%" height={16} /><Skeleton width="80%" height={40} /></div>
        : q.error ? <Failure error={q.error} title="Could not load unmarked sessions" onRetry={() => void q.refetch()} />
        : q.data.length === 0 ? <EmptyState compact icon="check" title="Nothing unmarked" description="Every past session has an outcome." />
        : (
          <div className={c.list} role="list">
            <FormError message={error} />
            {q.data.map((s: PtSession) => (
              <div key={s.id} className={c.li} role="listitem">
                <div>
                  <strong>{s.member?.name ?? "Member"}</strong>
                  <span className={c.sub}>{formatDate(s.date, sc.ctx.locale, "weekday")} {formatDate(s.date, sc.ctx.locale, "short")} · {s.start}–{s.end} · {nameOf(s.trainerId, s.trainerName)}</span>
                </div>
                <div className={k.acts}>
                  <Button size="sm" aria-label={`Complete ${s.member?.name ?? "session"}, ${s.date}`} busy={mark.isPending && mark.variables?.id === s.id} onClick={() => mark.mutate({ id: s.id, kind: "complete" })}>Complete</Button>
                  <Button size="sm" aria-label={`No-show ${s.member?.name ?? "session"}, ${s.date}`} busy={mark.isPending && mark.variables?.id === s.id} onClick={() => mark.mutate({ id: s.id, kind: "no_show" })}>No-show</Button>
                  <Button size="sm" variant="plain" onClick={() => onOpen(s)}>Open</Button>
                </div>
              </div>
            ))}
          </div>
        )}
    </Card>
  );
}

/** A trainer's booked sessions that fall outside their hours (leave, a short day). One tap hands one to a free trainer; the member keeps the session. */
export function LeaveImpact({ trainers, nameOf, onOpen }: { trainers: string[]; nameOf: (id: string | null) => string; onOpen: (s: PtSession) => void }) {
  const { client } = useServices();
  const sc = useScope();
  const qc = useQueryClient();
  const toast = useToast();
  const me = sc.ctx.me?.staff.id;
  const [pick, setPick] = useState("");
  const [to, setTo] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const trainerId = trainers.includes(pick) ? pick : me && trainers.includes(me) ? me : trainers[0] ?? "";
  const q = useQuery({
    queryKey: ["pt", "affected", ...sc.key, trainerId, sc.date],
    queryFn: () => listAffected(client, { trainerId, from: sc.date, to: addDays(sc.date, 60) }).then((r) => r.affected),
    enabled: trainerId !== "",
    retry: false,
  });
  const reassign = useMutation({
    mutationFn: (v: { id: string; trainerId: string }) => changePtTrainer(client, v.id, { trainerId: v.trainerId, scope: "session" }),
    onSuccess: () => { setError(null); void qc.invalidateQueries({ queryKey: ["pt"] }); toast.show("Session handed over"); },
    onError: (e) => setError(messageOf(e)),
  });
  if (trainers.length === 0) return null;
  return (
    <Card aria-label="Sessions affected by leave">
      <CardHeader title="Affected by leave" subtitle="Booked sessions in the next 60 days that fall outside the trainer's hours." />
      <div className={`${c.stack} ${c.pad}`}>
        {trainers.length > 1 ? <Field label="Trainer on leave" htmlFor="leave-trainer"><select id="leave-trainer" className={c.input} value={trainerId} onChange={(e) => setPick(e.target.value)}>{trainers.map((t) => <option key={t} value={t}>{nameOf(t)}</option>)}</select></Field> : null}
        <FormError message={error} />
      </div>
      {q.isPending ? <div aria-busy="true" aria-label="Loading affected sessions" className={k.skGrid}><Skeleton width="40%" height={16} /><Skeleton width="80%" height={40} /></div>
        : q.error ? <Failure error={q.error} title="Could not load affected sessions" onRetry={() => void q.refetch()} />
        : q.data.length === 0 ? <EmptyState compact icon="check" title="No clashes" description={`${nameOf(trainerId)}'s booked sessions all fit their hours.`} />
        : (
          <div className={c.list} role="list">
            {q.data.map(({ session: s, alternates }) => {
              const choice = to[s.id] && alternates.includes(to[s.id]!) ? to[s.id]! : alternates[0] ?? "";
              return (
                <div key={s.id} className={c.li} role="listitem">
                  <div>
                    <strong>{s.member?.name ?? "Member"}</strong>
                    <span className={c.sub}>{formatDate(s.date, sc.ctx.locale, "weekday")} {formatDate(s.date, sc.ctx.locale, "short")} · {s.start}–{s.end}</span>
                  </div>
                  <div className={k.acts}>
                    {alternates.length > 0 ? (
                      <>
                        <select aria-label={`Hand ${s.member?.name ?? "session"} on ${s.date} to`} className={c.input} value={choice} onChange={(e) => setTo({ ...to, [s.id]: e.target.value })}>{alternates.map((t) => <option key={t} value={t}>{nameOf(t)}</option>)}</select>
                        <Button size="sm" aria-label={`Reassign ${s.member?.name ?? "session"}, ${s.date}`} busy={reassign.isPending && reassign.variables?.id === s.id} onClick={() => reassign.mutate({ id: s.id, trainerId: choice })}>Reassign</Button>
                      </>
                    ) : <span className={c.note}>No trainer is free then</span>}
                    <Button size="sm" variant="plain" onClick={() => onOpen(s)}>Shift</Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </Card>
  );
}
